import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Layout, Tree, Button, Input, Select, Table, Tag, Space, App as AntdApp,
  Dropdown, Empty, Segmented, Tooltip, Spin, Drawer, Grid,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  Plus, Search, Filter, FileText, Download, Archive, ArchiveRestore, Trash2, MoreVertical,
  Eye, Upload, FileSignature, CheckCircle, Clock, XCircle, Lock,
  Briefcase, Folder, FolderOpen, Menu as MenuIcon, LayoutList, LayoutGrid,
  ArrowLeftRight, ChevronsDownUp,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { PageHeader, Card } from '@/components/common/PageHeader';
import { documentService, type DocumentListItem, type DocumentStatus } from '@/services/documentService';
import { orgService } from '@/services/rolesService';
import { CategoryTreeSelect } from '@/components/documents/CategoryTreeSelect';
import { formatDistanceToNow } from '@/utils/date';
import { useColumnConfig, type ColumnDef } from '@/hooks/useColumnConfig';
import { CreateDocumentModal } from '@/components/documents/CreateDocumentModal';

const { Sider, Content } = Layout;

const STATUS_LABELS: Record<DocumentStatus, { label: string; color: string; icon: any }> = {
  DRAFT:             { label: 'Nháp',          color: 'default', icon: FileText },
  PENDING_APPROVAL:  { label: 'Chờ duyệt',     color: 'processing', icon: Clock },
  APPROVED:          { label: 'Đã duyệt',      color: 'success', icon: CheckCircle },
  SIGNED:            { label: 'Đã ký',         color: 'blue', icon: FileSignature },
  REJECTED:          { label: 'Từ chối',       color: 'error', icon: XCircle },
  ARCHIVED:          { label: 'Lưu trữ',       color: 'default', icon: Archive },
};

// V1.0.65: 4-level visibility labels with icons
const CONFIDENTIALITY_LABELS: Record<string, { label: string; color: string; icon: string }> = {
  PUBLIC:       { label: 'Công khai', color: 'green',  icon: '🌐' },
  INTERNAL:     { label: 'Nội bộ',    color: 'blue',   icon: '🏢' },
  SHARED:       { label: 'Chia sẻ',   color: 'purple', icon: '🔗' },
  CONFIDENTIAL: { label: 'Mật',       color: 'red',    icon: '🔒' },
};

function formatBytes(bytes?: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function DocumentsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { message } = AntdApp.useApp();
  const qc = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [categoryId, setCategoryId] = useState<string | undefined>(searchParams.get('categoryId') ?? undefined);
  const [departmentId, setDepartmentId] = useState<string | undefined>(searchParams.get('departmentId') ?? undefined);
  const [status, setStatus] = useState<DocumentStatus | undefined>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);

  // View mode persisted to localStorage
  const [docViewMode, setDocViewMode] = useState<'list' | 'kanban'>(() => {
    const saved = localStorage.getItem('hv-doc-view-mode');
    return (saved === 'kanban' || saved === 'list') ? saved : 'list';
  });
  useEffect(() => { localStorage.setItem('hv-doc-view-mode', docViewMode); }, [docViewMode]);

  // Mobile drawer
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [mobileSiderOpen, setMobileSiderOpen] = useState(false);

  // V1.0.31 + V1.0.32: Tree mode
  //  - 'dept-cat': Khoa → Danh mục
  //  - 'cat-dept': Danh mục → Khoa
  //  - 'group-dept-cat': Nhóm → Khoa → Danh mục (V1.0.32)
  // V1.0.33: Mặc định = group-dept-cat (gom theo nhóm) — UX nhất quán với Projects
  const [treeGroupMode, setTreeGroupMode] = useState<'dept-cat' | 'cat-dept' | 'group-dept-cat'>(() => {
    const saved = localStorage.getItem('hv-doc-tree-group');
    if (saved === 'dept-cat' || saved === 'cat-dept' || saved === 'group-dept-cat') return saved;
    // User chưa từng chọn → default group-dept-cat
    return 'group-dept-cat';
  });
  useEffect(() => { localStorage.setItem('hv-doc-tree-group', treeGroupMode); }, [treeGroupMode]);

  // V1.0.31: Expand/collapse state — null = mặc định expand all, [] = collapse all, [keys] = chỉ mở các key này
  const [expandedKeys, setExpandedKeys] = useState<React.Key[] | null>(null);

  // Load org tree (chỉ hiện dept user có quyền xem)
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree-public'],
    queryFn: orgService.getTree,
  });

  // V1.0.32: Load danh sách nhóm khoa phòng để gom 1 cấp
  const { data: deptGroups = [] } = useQuery({
    queryKey: ['dept-groups'],
    queryFn: orgService.listGroups,
  });

  // Sync URL với filters
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (categoryId) params.set('categoryId', categoryId); else params.delete('categoryId');
    if (departmentId) params.set('departmentId', departmentId); else params.delete('departmentId');
    setSearchParams(params, { replace: true });
  }, [categoryId, departmentId]);

  const { data: categories = [] } = useQuery({
    queryKey: ['doc-categories'],
    queryFn: documentService.listCategories,
  });

  // V1.0.65: Mode switch giữa danh sách bình thường và "Chia sẻ với tôi"
  const [filterMode, setFilterMode] = useState<'all' | 'shared-with-me'>('all');

  const { data: listData, isLoading } = useQuery({
    queryKey: ['documents', filterMode, { keyword, categoryId, departmentId, status, includeArchived, page }],
    queryFn: () => {
      if (filterMode === 'shared-with-me') {
        // V1.0.65: Tab "Được chia sẻ với tôi"
        return documentService.listSharedWithMe({
          keyword: keyword.trim() || undefined,
          page,
          pageSize: 20,
        }).then((r: any) => ({
          items: r.items,
          total: r.total,
          page: r.page,
          pageSize: r.pageSize,
        }));
      }
      return documentService.list({
        keyword: keyword.trim() || undefined,
        categoryId,
        departmentId,
        status,
        archivedOnly: includeArchived,
        page,
        pageSize: 20,
      });
    },
  });

  const items = listData?.items ?? [];
  const total = listData?.total ?? 0;

  // Stats (đếm theo status) từ toàn bộ (không filter) - dùng query riêng không filter
  const { data: allStats } = useQuery({
    queryKey: ['documents-stats'],
    queryFn: () => documentService.list({ pageSize: 1 }),
  });

  const handleDownload = async (doc: DocumentListItem) => {
    try {
      const blob = await documentService.download(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc.docCode}.${doc.currentFileMime?.includes('pdf') ? 'pdf' : 'bin'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Không tải được');
    }
  };

  const handleArchive = async (id: string, title: string) => {
    try {
      await documentService.archive(id);
      message.success(`Đã lưu trữ "${title}"`);
      qc.invalidateQueries({ queryKey: ['documents'] });
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    }
  };

  const handleUnarchive = async (id: string, title: string) => {
    try {
      await documentService.unarchive(id);
      message.success(`Đã đẩy "${title}" về hoạt động`);
      qc.invalidateQueries({ queryKey: ['documents'] });
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Lỗi');
    }
  };

  // ============ COLUMN CONFIG ============
  const ALL_DOC_COLUMNS: ColumnDef<DocumentListItem>[] = useMemo(() => [
    {
      key: 'docCode', label: 'Mã tài liệu', defaultVisible: true, width: 160,
      render: (code: string, doc: DocumentListItem) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: '#6699CC' }}>
            {code}
          </div>
          {doc.categoryCode && (
            <Tag color={doc.categoryColor ?? 'default'} style={{ marginTop: 2, fontSize: 10 }}>
              {doc.categoryName}
            </Tag>
          )}
        </div>
      ),
      excelValue: (d) => d.docCode,
    },
    {
      key: 'title', label: 'Tiêu đề', defaultVisible: true,
      render: (title: string, doc: DocumentListItem) => (
        <div>
          <a onClick={() => navigate(`/documents/${doc.id}`)}
             style={{ fontSize: 13, fontWeight: 500, color: '#1a1d24' }}>
            {title}
          </a>
          <div style={{ fontSize: 11, color: '#8a95a5', marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>v{doc.currentVersion}</span>
            <span>·</span>
            <span>{formatBytes(doc.currentFileSize)}</span>
            {doc.hasSignedVersion && (
              <>
                <span>·</span>
                <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                  <FileSignature size={9} style={{ verticalAlign: -1 }} /> Đã ký
                </Tag>
              </>
            )}
          </div>
        </div>
      ),
      excelValue: (d) => d.title,
    },
    {
      key: 'categoryName', label: 'Loại tài liệu', defaultVisible: false, width: 140,
      render: (_: any, doc: DocumentListItem) => doc.categoryName ?? '-',
      excelValue: (d) => d.categoryName ?? '',
    },
    {
      key: 'status', label: 'Trạng thái', defaultVisible: true, width: 130,
      render: (s: DocumentStatus) => {
        const cfg = STATUS_LABELS[s];
        if (!cfg) return <Tag>{s}</Tag>;
        const Icon = cfg.icon;
        return (
          <Tag color={cfg.color} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon size={11} /> {cfg.label}
          </Tag>
        );
      },
      excelValue: (d) => STATUS_LABELS[d.status]?.label ?? d.status,
    },
    {
      key: 'confidentiality', label: 'Bảo mật', defaultVisible: true, width: 90,
      render: (c: string) => {
        const cfg = CONFIDENTIALITY_LABELS[c] ?? { label: c, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
      excelValue: (d) => CONFIDENTIALITY_LABELS[d.confidentiality]?.label ?? d.confidentiality,
    },
    {
      key: 'ownerName', label: 'Chủ sở hữu', defaultVisible: true, width: 160,
      render: (name: string, doc: DocumentListItem) => (
        <div style={{ fontSize: 12 }}>
          <div>{name}</div>
          {doc.departmentName && (
            <div style={{ color: '#8a95a5', fontSize: 11 }}>{doc.departmentName}</div>
          )}
        </div>
      ),
      excelValue: (d) => d.ownerName ?? '',
    },
    {
      key: 'departmentName', label: 'Khoa/Phòng', defaultVisible: false, width: 140,
      render: (_: any, doc: DocumentListItem) => doc.departmentName ?? '-',
      excelValue: (d) => d.departmentName ?? '',
    },
    {
      key: 'currentVersion', label: 'Phiên bản', defaultVisible: false, width: 80,
      render: (v: number) => `v${v}`,
      excelValue: (d) => `v${d.currentVersion}`,
    },
    {
      key: 'currentFileSize', label: 'Kích thước', defaultVisible: false, width: 100,
      render: (s: number) => formatBytes(s),
      excelValue: (d) => formatBytes(d.currentFileSize),
    },
    {
      key: 'createdAt', label: 'Ngày tạo', defaultVisible: false, width: 110,
      render: (d: string) => d ? <span style={{ fontSize: 11 }}>{dayjs(d).format('DD/MM/YYYY')}</span> : '-',
      excelValue: (d: any) => d.createdAt ? dayjs(d.createdAt).format('DD/MM/YYYY') : '',
    },
    {
      key: 'updatedAt', label: 'Cập nhật', defaultVisible: true, width: 110,
      render: (d: string) => <span style={{ fontSize: 11, color: '#7d8a98' }}>{formatDistanceToNow(d)}</span>,
      excelValue: (d) => d.updatedAt ? dayjs(d.updatedAt).format('DD/MM/YYYY HH:mm') : '',
    },
  ], [navigate]);

  const { antColumns, exportExcel, ColumnConfigButton, ColumnConfigModal } =
    useColumnConfig<DocumentListItem>(ALL_DOC_COLUMNS, 'hv-taskflow-documents-columns-v1');

  const columnsWithAction: ColumnsType<DocumentListItem> = useMemo(() => [
    ...antColumns,
    {
      title: '',
      key: 'actions',
      width: 60,
      fixed: 'right' as const,
      render: (_, doc) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'view', icon: <Eye size={14} />, label: 'Xem chi tiết',
                onClick: () => navigate(`/documents/${doc.id}`) },
              { key: 'download', icon: <Download size={14} />, label: 'Tải xuống',
                onClick: () => handleDownload(doc) },
              { type: 'divider' as const },
              doc.archived
                ? { key: 'unarchive', icon: <ArchiveRestore size={14} />,
                    label: 'Đẩy về hoạt động',
                    onClick: () => handleUnarchive(doc.id, doc.title) }
                : { key: 'archive', icon: <Archive size={14} />, label: 'Lưu trữ', danger: true,
                    onClick: () => handleArchive(doc.id, doc.title) },
            ],
          }}
        >
          <Button type="text" icon={<MoreVertical size={14} />} />
        </Dropdown>
      ),
    },
  ], [antColumns, navigate]);

  const handleExportDocs = () => {
    if (items.length === 0) { message.warning('Không có dữ liệu để xuất'); return; }
    exportExcel(items, 'Danh sách tài liệu', 'documents');
    message.success(`Đã xuất ${items.length} tài liệu`);
  };

  const statusCards = useMemo(() => {
    // Count theo status dùng client side trên items hiện tại (page đầu)
    // Thực tế nên có endpoint /documents/stats, MVP em tính trên kết quả đang hiện
    const counts: Record<string, number> = {};
    items.forEach((i: any) => { counts[i.status] = (counts[i.status] ?? 0) + 1; });
    return [
      { key: 'DRAFT', ...STATUS_LABELS.DRAFT, count: counts.DRAFT ?? 0 },
      { key: 'PENDING_APPROVAL', ...STATUS_LABELS.PENDING_APPROVAL, count: counts.PENDING_APPROVAL ?? 0 },
      { key: 'APPROVED', ...STATUS_LABELS.APPROVED, count: counts.APPROVED ?? 0 },
      { key: 'SIGNED', ...STATUS_LABELS.SIGNED, count: counts.SIGNED ?? 0 },
    ];
  }, [items]);

  // Build tree dữ liệu cho Sider:
  // V1.0.31 + V1.0.32: Build treeData theo 3 mode:
  //   - 'dept-cat': Khoa → Danh mục
  //   - 'cat-dept': Danh mục → Khoa
  //   - 'group-dept-cat': Nhóm → Khoa → Danh mục (V1.0.32)
  const treeData = useMemo(() => {
    const activeCats = categories.filter(c => c.isActive !== false);

    // Helper: build node cấp cuối (cat-) gắn vào dept
    const buildCatChildrenForDept = (deptId: string) => activeCats.map(cat => ({
      key: `cat-${deptId}-${cat.id}`,
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <Folder size={12} style={{ color: cat.color ?? '#7d8a98', flexShrink: 0 }} />
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cat.name}
          </span>
        </span>
      ),
      isLeaf: true,
      deptId,
      categoryId: cat.id,
    }));

    // Helper: build node Khoa (level 1 trong dept-cat / level 2 trong group-dept-cat)
    const buildDeptNode = (dept: any): any => ({
      key: `dept-${dept.id}`,
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <Briefcase size={14} style={{ color: '#5b6875', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, flex: 1, color: '#1a1e23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {dept.name}
          </span>
          {dept.groupName && treeGroupMode === 'dept-cat' && (
            <span style={{
              fontSize: 9, padding: '0 6px', borderRadius: 8,
              background: (dept.groupColor ?? '#e8edf2') + '20',
              color: dept.groupColor ?? '#5b6875',
              border: `1px solid ${dept.groupColor ?? '#e8edf2'}40`,
              flexShrink: 0,
            }}>
              {dept.groupName}
            </span>
          )}
        </span>
      ),
      children: buildCatChildrenForDept(dept.id),
      selectable: true,
    });

    if (treeGroupMode === 'dept-cat') {
      return orgTree.map(buildDeptNode);
    }

    if (treeGroupMode === 'group-dept-cat') {
      // Flatten depts (lấy hết khoa, không quan tâm parent)
      const flatDepts: any[] = [];
      const walkOrg = (n: any) => {
        flatDepts.push(n);
        n.children?.forEach(walkOrg);
      };
      orgTree.forEach(walkOrg);

      // Gom depts theo groupId
      const deptsByGroup = new Map<string, any[]>();
      const ungrouped: any[] = [];
      flatDepts.forEach(d => {
        if (d.groupId) {
          if (!deptsByGroup.has(d.groupId)) deptsByGroup.set(d.groupId, []);
          deptsByGroup.get(d.groupId)!.push(d);
        } else {
          ungrouped.push(d);
        }
      });

      const nodes: any[] = [];
      // Theo thứ tự sortOrder của groups
      deptGroups
        .filter(g => g.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(g => {
          const depts = deptsByGroup.get(g.id) ?? [];
          if (depts.length === 0) return; // skip nhóm trống
          nodes.push({
            key: `grp-${g.id}`,
            title: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
                <FolderOpen size={14} style={{ color: g.color, flexShrink: 0 }} />
                <span style={{ fontWeight: 700, flex: 1, color: g.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {g.name}
                </span>
                <span style={{ fontSize: 10, color: '#7d8a98', flexShrink: 0 }}>
                  ({depts.length})
                </span>
              </span>
            ),
            selectable: false,
            children: depts.map(buildDeptNode),
          });
        });
      if (ungrouped.length > 0) {
        nodes.push({
          key: 'grp-none',
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Folder size={14} style={{ color: '#bdc4cd', flexShrink: 0 }} />
              <span style={{ fontWeight: 700, color: '#5b6875' }}>Chưa phân nhóm</span>
              <span style={{ fontSize: 10, color: '#7d8a98' }}>({ungrouped.length})</span>
            </span>
          ),
          selectable: false,
          children: ungrouped.map(buildDeptNode),
        });
      }
      return nodes;
    }

    // 'cat-dept': Danh mục → Khoa
    const flatDepts: any[] = [];
    const walkOrg = (n: any) => {
      flatDepts.push(n);
      n.children?.forEach(walkOrg);
    };
    orgTree.forEach(walkOrg);

    return activeCats.map(cat => ({
      key: `catRoot-${cat.id}`,
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <Folder size={14} style={{ color: cat.color ?? '#5b6875', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, flex: 1, color: '#1a1e23', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cat.name}
          </span>
        </span>
      ),
      children: flatDepts.map(dept => ({
        key: `catdept-${cat.id}-${dept.id}`,
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: '100%' }}>
            <Briefcase size={12} style={{ color: '#7d8a98', flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {dept.name}
            </span>
          </span>
        ),
        isLeaf: true,
        deptId: dept.id,
        categoryId: cat.id,
      })),
      selectable: true,
      categoryId: cat.id,
    }));
  }, [orgTree, categories, treeGroupMode, deptGroups]);

  // Build full list of expand keys cho expand-all
  const allExpandKeys = useMemo(() => {
    if (treeGroupMode === 'dept-cat') {
      return orgTree.map((d: any) => `dept-${d.id}`);
    }
    if (treeGroupMode === 'group-dept-cat') {
      // Mở nhóm + tất cả khoa trong nhóm
      const keys: string[] = [];
      deptGroups.filter(g => g.isActive).forEach(g => keys.push(`grp-${g.id}`));
      keys.push('grp-none');
      const flatDepts: any[] = [];
      const walkOrg = (n: any) => { flatDepts.push(n); n.children?.forEach(walkOrg); };
      orgTree.forEach(walkOrg);
      flatDepts.forEach(d => keys.push(`dept-${d.id}`));
      return keys;
    }
    return categories.filter(c => c.isActive !== false).map(c => `catRoot-${c.id}`);
  }, [orgTree, categories, treeGroupMode, deptGroups]);

  // Selected key cho tree
  const selectedKeys = useMemo(() => {
    if (treeGroupMode === 'dept-cat') {
      if (departmentId && categoryId) return [`cat-${departmentId}-${categoryId}`];
      if (departmentId) return [`dept-${departmentId}`];
      return [];
    } else {
      if (departmentId && categoryId) return [`catdept-${categoryId}-${departmentId}`];
      if (categoryId) return [`catRoot-${categoryId}`];
      return [];
    }
  }, [departmentId, categoryId, treeGroupMode]);

  const onTreeSelect = (_keys: any[], info: any) => {
    const key = info.node?.key as string | undefined;
    if (!key) return;
    if (key.startsWith('cat-') && !key.startsWith('catRoot-') && !key.startsWith('catdept-')) {
      // cat-<deptId>-<catId> (mode dept-cat)
      const node = info.node;
      setDepartmentId(node.deptId);
      setCategoryId(node.categoryId);
    } else if (key.startsWith('dept-')) {
      setDepartmentId(key.substring(5));
      setCategoryId(undefined);
    } else if (key.startsWith('catdept-')) {
      // catdept-<catId>-<deptId> (mode cat-dept)
      const node = info.node;
      setCategoryId(node.categoryId);
      setDepartmentId(node.deptId);
    } else if (key.startsWith('catRoot-')) {
      // catRoot-<catId> — chỉ filter theo category
      const node = info.node;
      setCategoryId(node.categoryId);
      setDepartmentId(undefined);
    }
    if (isMobile) setMobileSiderOpen(false);
  };

  const clearFilters = () => {
    setDepartmentId(undefined);
    setCategoryId(undefined);
  };

  // Sider content (dùng chung desktop + drawer mobile)
  const siderContent = (
    <>
      <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 12, color: '#7d8a98', textTransform: 'uppercase' }}>
            {treeGroupMode === 'dept-cat'
              ? 'Khoa → Danh mục'
              : treeGroupMode === 'group-dept-cat'
                ? 'Nhóm → Khoa → Danh mục'
                : 'Danh mục → Khoa'}
          </strong>
          {(departmentId || categoryId) && (
            <Button size="small" type="link" onClick={clearFilters} style={{ padding: 0, height: 'auto', fontSize: 11 }}>
              Xóa lọc
            </Button>
          )}
        </div>

        {/* V1.0.31 + V1.0.32: Toggle 3 mode + Expand/Collapse */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Tooltip title="Đổi cách gom (3 chế độ: Khoa→DM / Nhóm→Khoa→DM / DM→Khoa)">
            <Button
              size="small"
              icon={<ArrowLeftRight size={12} />}
              onClick={() => {
                setTreeGroupMode(prev => {
                  if (prev === 'dept-cat') return 'group-dept-cat';
                  if (prev === 'group-dept-cat') return 'cat-dept';
                  return 'dept-cat';
                });
                setExpandedKeys(null);
              }}
              style={{ fontSize: 11, padding: '0 6px' }}
            >
              Đổi
            </Button>
          </Tooltip>
          <Tooltip title="Mở rộng tất cả">
            <Button
              size="small"
              icon={<ChevronsDownUp size={12} style={{ transform: 'rotate(180deg)' }} />}
              onClick={() => setExpandedKeys(allExpandKeys)}
              style={{ fontSize: 11, padding: '0 6px' }}
            >
              Mở
            </Button>
          </Tooltip>
          <Tooltip title="Thu gọn tất cả">
            <Button
              size="small"
              icon={<ChevronsDownUp size={12} />}
              onClick={() => setExpandedKeys([])}
              style={{ fontSize: 11, padding: '0 6px' }}
            >
              Gọn
            </Button>
          </Tooltip>
        </div>
      </div>
      {orgTree.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : (
        <Tree
          blockNode
          treeData={treeData}
          onSelect={onTreeSelect}
          selectedKeys={selectedKeys}
          // V1.0.31: controlled expand. Nếu null → dùng defaultExpandAll behavior
          {...(expandedKeys === null
            ? { defaultExpandAll: true }
            : { expandedKeys, onExpand: (keys: React.Key[]) => setExpandedKeys(keys) }
          )}
          className="hv-projects-tree"
          style={{ padding: '8px 4px', background: 'transparent' }}
        />
      )}
    </>
  );

  return (
    <Layout style={{ height: 'calc(100vh - 64px)', background: '#f5f5f5' }}>
      {/* DESKTOP Sider */}
      {!isMobile && (
        <Sider
          width={280}
          theme="light"
          style={{ borderRight: '1px solid #e5e5e5', overflow: 'auto', background: '#fff' }}
        >
          {siderContent}
        </Sider>
      )}

      {/* MOBILE Drawer */}
      {isMobile && (
        <Drawer
          title="Phòng ban / Danh mục"
          placement="left"
          width="85%"
          open={mobileSiderOpen}
          onClose={() => setMobileSiderOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          {siderContent}
        </Drawer>
      )}

      <Content style={{ overflow: 'auto', padding: '12px 16px' }}>
        {isMobile && (
          <div style={{ marginBottom: 8 }}>
            <Button size="small" icon={<MenuIcon size={14} />} onClick={() => setMobileSiderOpen(true)}>
              {departmentId ? 'Đổi khoa/danh mục' : 'Chọn khoa/danh mục'}
            </Button>
          </div>
        )}

      <PageHeader
        title="Quản lý tài liệu"
        subtitle={
          departmentId
            ? `Lọc theo: ${orgTree.flatMap(walkOrg).find(d => d.id === departmentId)?.name ?? ''}${categoryId ? ` · ${categories.find(c => c.id === categoryId)?.name ?? ''}` : ''} · ${total} tài liệu`
            : `${total} tài liệu · Kho lưu trữ tập trung EDM`
        }
        extra={
          <Space>
            <ColumnConfigButton />
            <Button icon={<Download size={14} />} onClick={handleExportDocs}>
              Xuất Excel
            </Button>
            <Button icon={<FolderOpen size={14} />} onClick={() => navigate('/documents/categories')}>
              Danh mục
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
              Tạo tài liệu
            </Button>
          </Space>
        }
      />

      {/* Quick stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        {statusCards.map(card => {
          const Icon = card.icon;
          return (
            <Card key={card.key} padding={14}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Icon size={22} style={{ color: '#6699CC' }} />
                <div>
                  <div style={{ fontSize: 11, color: '#7d8a98' }}>{card.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{card.count}</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card>
        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <Input
            prefix={<Search size={14} style={{ color: '#8a95a5' }} />}
            placeholder="Tìm theo mã, tiêu đề, mô tả..."
            style={{ width: 280 }}
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
            allowClear
          />
          <CategoryTreeSelect
            categories={categories}
            value={categoryId}
            onChange={setCategoryId}
            placeholder="Loại tài liệu"
            width={220}
          />
          <Select
            placeholder="Trạng thái"
            style={{ width: 150 }}
            value={status}
            onChange={setStatus}
            allowClear
            options={Object.entries(STATUS_LABELS).map(([k, v]) => ({
              value: k, label: v.label,
            }))}
          />
          {/* V1.0.65: Mode switch - Tất cả vs Được chia sẻ với tôi */}
          <Segmented
            options={[
              { label: '📋 Tất cả', value: 'all' },
              { label: '🔗 Được chia sẻ với tôi', value: 'shared-with-me' },
            ]}
            value={filterMode}
            onChange={(v) => {
              setFilterMode(v as 'all' | 'shared-with-me');
              setPage(1);
            }}
          />
          {filterMode === 'all' && (
            <Segmented
              options={[
                { label: 'Đang hoạt động', value: 'active' },
                { label: 'Lưu trữ', value: 'archived' },
              ]}
              value={includeArchived ? 'archived' : 'active'}
              onChange={v => setIncludeArchived(v === 'archived')}
            />
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Segmented
              size="middle"
              value={docViewMode}
              onChange={(v) => setDocViewMode(v as 'list' | 'kanban')}
              options={[
                { value: 'list', icon: <LayoutList size={14} />, label: '' },
                { value: 'kanban', icon: <LayoutGrid size={14} />, label: '' },
              ]}
            />
          </div>
        </div>

        {/* List view = Table */}
        {docViewMode === 'list' && (
          <Table
            dataSource={items}
            columns={columnsWithAction}
            rowKey="id"
            loading={isLoading}
            size="middle"
            pagination={{
              current: page,
              pageSize: 20,
              total,
              showSizeChanger: false,
              onChange: setPage,
              showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
            }}
            locale={{
              emptyText: <Empty description="Chưa có tài liệu nào" />,
            }}
            onRow={(doc) => ({
              onDoubleClick: () => navigate(`/documents/${doc.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        )}

        {/* Kanban view = group theo category */}
        {docViewMode === 'kanban' && (
          <DocumentsKanban
            items={items}
            categories={categories}
            loading={isLoading}
            onClick={(id) => navigate(`/documents/${id}`)}
          />
        )}
      </Card>

      {createOpen && (
        <CreateDocumentModal
          defaultDepartmentId={departmentId}
          defaultCategoryId={categoryId}
          onClose={() => setCreateOpen(false)}
          onCreated={(doc) => {
            setCreateOpen(false);
            qc.invalidateQueries({ queryKey: ['documents'] });
            navigate(`/documents/${doc.id}`);
          }}
        />
      )}
      <ColumnConfigModal />
      </Content>
    </Layout>
  );
}

/** Helper: flatten org tree để tìm dept theo id */
function walkOrg(node: any): any[] {
  return [node, ...(node.children ?? []).flatMap(walkOrg)];
}

/**
 * Kanban view group documents theo category.
 * Mỗi cột = 1 category với màu riêng, scroll dọc trong cột.
 */
function DocumentsKanban({ items, categories, loading, onClick }: {
  items: DocumentListItem[];
  categories: any[];
  loading: boolean;
  onClick: (id: string) => void;
}) {
  // Group items by categoryCode
  const grouped = useMemo(() => {
    const map = new Map<string, DocumentListItem[]>();
    items.forEach(d => {
      const key = d.categoryCode ?? '__uncategorized__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    });
    return map;
  }, [items]);

  // Active categories = các category có items hoặc đang được filter
  const activeCategories = useMemo(() => {
    const codes = new Set(items.map(d => d.categoryCode).filter(Boolean) as string[]);
    return categories.filter(c => codes.has(c.code));
  }, [categories, items]);

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }
  if (items.length === 0) {
    return <Empty description="Chưa có tài liệu nào" style={{ marginTop: 40 }} />;
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.max(activeCategories.length, 1)}, minmax(260px, 1fr))`,
      gap: 12,
      overflowX: 'auto',
      paddingBottom: 8,
    }}>
      {activeCategories.map(cat => {
        const colItems = grouped.get(cat.code) ?? [];
        const color = cat.color ?? '#1976D2';
        return (
          <div key={cat.id} style={{
            background: '#fff',
            border: '1px solid #e8edf2',
            borderTop: `3px solid ${color}`,
            borderRadius: 8,
            padding: 10,
            minHeight: 320,
            display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <div style={{
              padding: '4px 8px',
              fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
              color, textTransform: 'uppercase',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>{cat.name}</span>
              <span style={{
                background: `${color}15`, color,
                borderRadius: 10, padding: '0 8px',
                fontSize: 11, minWidth: 22, textAlign: 'center',
              }}>{colItems.length}</span>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '70vh', overflowY: 'auto' }}>
              {colItems.map(doc => {
                const StatusIcon = STATUS_LABELS[doc.status]?.icon ?? FileText;
                return (
                  <div
                    key={doc.id}
                    onClick={() => onClick(doc.id)}
                    style={{
                      background: '#fff',
                      border: '1px solid #e8edf2',
                      borderLeft: `3px solid ${color}`,
                      borderRadius: 6,
                      padding: '8px 10px',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <StatusIcon size={12} style={{ color, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#7d8a98', fontFamily: 'monospace' }}>
                        {doc.docCode}
                      </span>
                      <Tag color={STATUS_LABELS[doc.status]?.color} style={{ marginLeft: 'auto', fontSize: 10, padding: '0 6px' }}>
                        {STATUS_LABELS[doc.status]?.label}
                      </Tag>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1e23', marginBottom: 4, lineHeight: 1.3 }}>
                      {doc.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#7d8a98' }}>
                      <span>v{doc.currentVersion}</span>
                      <span>·</span>
                      <span>{doc.ownerName}</span>
                      {doc.departmentName && <><span>·</span><span>{doc.departmentName}</span></>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
